

import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Host,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NgClassInterface } from 'mwc/core/types';
import { isNotNil } from 'mwc/core/util';

import { MwcRowDirective } from './row.directive';

export interface EmbeddedProperty {
  span?: number;
  pull?: number;
  push?: number;
  offset?: number;
  order?: number;
}

@Directive({
  selector: '[mwc-col],mwc-col,mwc-form-control,mwc-form-label',
  exportAs: 'mwcCol',
  host: {
    '[style.flex]': 'hostFlexStyle'
  }
})
export class MwcColDirective implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  private classMap: { [key: string]: boolean } = {};
  private destroy$ = new Subject();
  hostFlexStyle: string | null = null;
  dir: Direction = 'ltr';
  @Input() mwcFlex: string | number | null = null;
  @Input() mwcSpan: string | number | null = null;
  @Input() mwcOrder: string | number | null = null;
  @Input() mwcOffset: string | number | null = null;
  @Input() mwcPush: string | number | null = null;
  @Input() mwcPull: string | number | null = null;
  @Input() mwcXs: string | number | EmbeddedProperty | null = null;
  @Input() mwcSm: string | number | EmbeddedProperty | null = null;
  @Input() mwcMd: string | number | EmbeddedProperty | null = null;
  @Input() mwcLg: string | number | EmbeddedProperty | null = null;
  @Input() mwcXl: string | number | EmbeddedProperty | null = null;
  @Input() mwcXXl: string | number | EmbeddedProperty | null = null;

  setHostClassMap(): void {
    const hostClassMap = {
      ['mwc-col']: true,
      [`mwc-col-${this.mwcSpan}`]: isNotNil(this.mwcSpan),
      [`mwc-col-order-${this.mwcOrder}`]: isNotNil(this.mwcOrder),
      [`mwc-col-offset-${this.mwcOffset}`]: isNotNil(this.mwcOffset),
      [`mwc-col-pull-${this.mwcPull}`]: isNotNil(this.mwcPull),
      [`mwc-col-push-${this.mwcPush}`]: isNotNil(this.mwcPush),
      ['mwc-col-rtl']: this.dir === 'rtl',
      ...this.generateClass()
    };
    for (const i in this.classMap) {
      if (this.classMap.hasOwnProperty(i)) {
        this.renderer.removeClass(this.elementRef.nativeElement, i);
      }
    }
    this.classMap = { ...hostClassMap };
    for (const i in this.classMap) {
      if (this.classMap.hasOwnProperty(i) && this.classMap[i]) {
        this.renderer.addClass(this.elementRef.nativeElement, i);
      }
    }
  }

  setHostFlexStyle(): void {
    this.hostFlexStyle = this.parseFlex(this.mwcFlex);
  }

  parseFlex(flex: number | string | null): string | null {
    if (typeof flex === 'number') {
      return `${flex} ${flex} auto`;
    } else if (typeof flex === 'string') {
      if (/^\d+(\.\d+)?(px|em|rem|%)$/.test(flex)) {
        return `0 0 ${flex}`;
      }
    }
    return flex;
  }

  generateClass(): object {
    const listOfSizeInputName: Array<keyof MwcColDirective> = ['mwcXs', 'mwcSm', 'mwcMd', 'mwcLg', 'mwcXl', 'mwcXXl'];
    const listClassMap: NgClassInterface = {};
    listOfSizeInputName.forEach(name => {
      const sizeName = name.replace('mwc', '').toLowerCase();
      if (isNotNil(this[name])) {
        if (typeof this[name] === 'number' || typeof this[name] === 'string') {
          listClassMap[`mwc-col-${sizeName}-${this[name]}`] = true;
        } else {
          const embedded = this[name] as EmbeddedProperty;
          const prefixArray: Array<keyof EmbeddedProperty> = ['span', 'pull', 'push', 'offset', 'order'];
          prefixArray.forEach(prefix => {
            const prefixClass = prefix === 'span' ? '-' : `-${prefix}-`;
            listClassMap[`mwc-col-${sizeName}${prefixClass}${embedded[prefix]}`] =
              embedded && isNotNil(embedded[prefix]);
          });
        }
      }
    });
    return listClassMap;
  }

  constructor(
    private elementRef: ElementRef,
    @Optional() @Host() public mwcRowDirective: MwcRowDirective,
    public renderer: Renderer2,
    @Optional() private directionality: Directionality
  ) {}

  ngOnInit(): void {
    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.setHostClassMap();
    });

    this.setHostClassMap();
    this.setHostFlexStyle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setHostClassMap();
    const { mwcFlex } = changes;
    if (mwcFlex) {
      this.setHostFlexStyle();
    }
  }

  ngAfterViewInit(): void {
    if (this.mwcRowDirective) {
      this.mwcRowDirective.actualGutter$
        .pipe(takeUntil(this.destroy$))
        .subscribe(([horizontalGutter, verticalGutter]) => {
          const renderGutter = (name: string, gutter: number | null): void => {
            const nativeElement = this.elementRef.nativeElement;
            if (gutter !== null) {
              this.renderer.setStyle(nativeElement, name, `${gutter / 2}px`);
            }
          };
          renderGutter('padding-left', horizontalGutter);
          renderGutter('padding-right', horizontalGutter);
          renderGutter('padding-top', verticalGutter);
          renderGutter('padding-bottom', verticalGutter);
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
